import {useContext, createContext, useState, useCallback} from 'react';
import {EnrichedAstNode} from 'rendition-core/types';
import {usePreviewListener} from './PreviewContext';
import {useRightClickState} from 'stores/rightClick';

export interface RightClickMenuDropdown {
    label: string;
    handlers: Array<RightClickMenuOption>;
    dropdown: true;
    condition?: RightClickMenuOption['condition'];
}

export interface RightClickMenuOption {
    label: string;
    handler: (nodes: EnrichedAstNode[]) => void;
    condition?: (nodes: EnrichedAstNode[]) => boolean;
}

export type RightClickMenuOptions = {label: string; condition?: RightClickMenuOption['condition']} & (
    | {
          handler?: never;
          handlers: Array<RightClickMenuOption>;
          dropdown: true;
      }
    | {
          handlers?: never;
          dropdown?: never;
          handler: RightClickMenuOption['handler'];
      }
);

export interface RightClickState {
    selectedNodeIds: string[];
    rightClickMenuOptions: Array<RightClickMenuOptions>;
    rightClickMenuCoordinates: {x: number; y: number};
    setRightClickMenuCoordinates: (coordinates: {x: number; y: number}) => void;
    rightClickMenuOpen: boolean;
    setRightClickMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
const RightClickMenuContext = createContext<RightClickState>({
    setRightClickMenuOpen: () => {},
    rightClickMenuOpen: false,
    selectedNodeIds: [],
    rightClickMenuOptions: [],
    rightClickMenuCoordinates: {x: 0, y: 0},
    setRightClickMenuCoordinates: () => {},
});

export const useRightClickMenuContext = () => {
    return useContext(RightClickMenuContext);
};

export const RightClickContextProvider: React.FC<
    React.PropsWithChildren<{rightClickMenuOptions: RightClickMenuOptions[]; selectedNodeIds: string[]}>
> = ({rightClickMenuOptions, children, selectedNodeIds}) => {
    const [rightClickMenuOpen, setRightClickMenuOpen] = useState(false);
    const [rightClickMenuCoordinates, setRightClickMenuCoordinates] = useState({x: 0, y: 0});

    const limitedRightClickIds = useRightClickState.use.limitedRightClickIds();

    const handleRightClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.MouseEvent, nodeId?: string) => {
            if (
                rightClickMenuOptions.length > 0 &&
                nodeId &&
                selectedNodeIds.includes(nodeId) &&
                (limitedRightClickIds.length === 0 || limitedRightClickIds.includes(nodeId))
            ) {
                event.preventDefault();
                event.stopPropagation();
                setRightClickMenuCoordinates({x: event.clientX, y: event.clientY});
                setRightClickMenuOpen(true);
                return true;
            }
            return false;
        },
        [limitedRightClickIds, rightClickMenuOptions.length, selectedNodeIds]
    );
    usePreviewListener('onNodeRightClick', handleRightClick);

    return (
        <RightClickMenuContext.Provider
            value={{
                rightClickMenuOpen,
                setRightClickMenuOpen,
                selectedNodeIds,
                rightClickMenuCoordinates,
                setRightClickMenuCoordinates,
                rightClickMenuOptions,
            }}
        >
            {children}
        </RightClickMenuContext.Provider>
    );
};
